import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'
import TiptapEditor from '../../../components/common/TiptapEditor'
import { parsePageToKioskSections } from '../../../utils/PageToKioskContent'

interface Attachment {
  id: number
  file_type: string
  file_url: string
  file_name: string | null
  sort_order: number | null
}

export default function AdminEventEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id && id !== 'new'

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isVisible, setIsVisible] = useState(1)
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([])
  const [deleteIds, setDeleteIds] = useState<number[]>([])
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isEdit) fetchEvent()
  }, [id])

  const fetchEvent = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/event/${id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setTitle(data.title)
      setContent(data.content)
      setIsVisible(data.is_visible)
      setExistingAttachments(data.attachments.filter((a: Attachment) => a.file_type === 'file'))
    } catch {
      alert('행사 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/event')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAttachment = (attachmentId: number) => {
    setDeleteIds(prev => [...prev, attachmentId])
    setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId))
  }

  const handleSubmit = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return }
    if (!content || content === '<p></p>') { alert('내용을 입력해주세요.'); return }

    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('content', content)
      formData.append('is_visible', String(isVisible))
      newFiles.forEach(file => formData.append('files', file))

      const token = localStorage.getItem('access_token')

      if (isEdit) {
        formData.append('replace_all', '0')
        deleteIds.forEach(delId => formData.append('delete_attachment_ids', String(delId)))

        const res = await fetch(getApiUrl(`/marubit/api/event/admin/${id}`), {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!res.ok) throw new Error('수정 실패')
        const data = await res.json()
        alert(data.message)
      } else {
        const res = await fetch(getApiUrl('/marubit/api/event/admin'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!res.ok) throw new Error('등록 실패')
        const data = await res.json()
        alert(data.message)
      }
      navigate('/admin/event')
    } catch {
      alert(isEdit ? '행사 수정에 실패했습니다.' : '행사 등록에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        <div className="mb-8">
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">
            {isEdit ? '행사 수정' : '행사 추가'}
          </h1>
          <p className="text-[13px] text-gray-400">
            {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </p>
        </div>

        {/* 제목 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">제목</h2>
          <input
            type="text"
            placeholder="행사 제목을 입력해주세요 *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
        </div>

        {/* 내용 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">내용</h2>
          <TiptapEditor
            onChange={setContent}
            initialContent={content}
            imageUploadUrl="/marubit/api/event/admin/upload-image"
          />
        </div>

        {/* 기존 첨부파일 */}
        {isEdit && existingAttachments.length > 0 && (
          <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">기존 첨부파일</h2>
            <ul className="space-y-2">
              {existingAttachments.map(a => (
                <li key={a.id} className="flex items-center justify-between text-[13px] text-gray-500 bg-[#f5f2f0] rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2">
                    <span className="text-[#887267]">📎</span>{a.file_name ?? '파일'}
                  </span>
                  <button
                    onClick={() => handleDeleteAttachment(a.id)}
                    className="text-[12px] text-red-400 hover:text-red-600 ml-4"
                  >
                    삭제
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 첨부파일 (등록 시 / 수정 시 추가) */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">
            {isEdit ? '첨부파일 추가' : '첨부파일'}
          </h2>
          <p className="text-[12px] text-gray-400 ml-[13px] mb-4">최대 5개까지 첨부 가능합니다.</p>
          <input
            type="file"
            multiple
            onChange={e => {
              const selected = Array.from(e.target.files ?? [])
              const merged = [...newFiles, ...selected]
              if (merged.length > 5) {
                alert('첨부파일은 최대 5개까지 가능합니다.')
                setNewFiles(merged.slice(0, 5))
              } else {
                setNewFiles(merged)
              }
              e.target.value = ''
            }}
            className="block text-[13px] text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-[13px] file:font-semibold file:bg-[#f5f2f0] file:text-[#887267] hover:file:bg-[#e8e0dc] cursor-pointer"
          />
          {newFiles.length > 0 && (
            <ul className="mt-3 space-y-2">
              {newFiles.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-[13px] text-gray-500 bg-[#f5f2f0] rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2"><span className="text-[#887267]">📎</span>{f.name}</span>
                  <button onClick={() => setNewFiles(newFiles.filter((_, idx) => idx !== i))} className="text-[12px] text-red-400 hover:text-red-600 ml-4">삭제</button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[12px] text-gray-300">{newFiles.length}/5</p>
        </div>

        {/* 노출 여부 */}
        <div className="bg-white rounded-2xl p-7 mb-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">노출 여부</h2>
          <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc] w-fit">
            <input
              type="checkbox"
              checked={isVisible === 1}
              onChange={e => setIsVisible(e.target.checked ? 1 : 0)}
              className="w-4 h-4 cursor-pointer accent-[#887267]"
            />
            <span className="text-[13px] font-semibold text-[#887267]">노출</span>
          </label>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          {isEdit && (
            <button
              onClick={() => {
                const { sections, firstImageUrl } = parsePageToKioskSections(content);
                navigate(`/admin/kiosk/content/register?kiosk_code=${localStorage.getItem("kiosk_code")}`, {
                  state: { title, sections, firstImageUrl }
                });
              }}
              className="flex-1 h-[52px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
            >
              키오스크 등록
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={[
              'flex-1 h-[52px] text-white border-none rounded-xl text-[16px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)]',
              submitting ? 'bg-[#b8a39b] cursor-not-allowed' : 'bg-[#887267] cursor-pointer hover:bg-[#6b5248]',
            ].join(' ')}
          >
            {submitting ? '저장 중...' : '저장'}
          </button>
          <button
            onClick={() => navigate('/admin/event')}
            className="flex-1 h-[52px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  )
}